// Route prefixes each admin module grants access to. The first prefix of a
// module doubles as its landing page. Keep in sync with the backend ADMIN_MODULE.
const ADMIN_MODULE_ROUTES = {
  stats: ['/admin/stats'],
  organizations: ['/admin/users'],
};

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      user: null,
      token: null,
      refresh: null,
    };
  },

  actions: {
    setAuth(payload) {
      if (payload) {
        const token = `Bearer ${payload.access}`;
        this.token = token;
        this.refresh = payload.refresh;

        window.localStorage.setItem('token', token);
        window.localStorage.setItem('refresh', payload.refresh);
      } else {
        this.token = null;
        this.refresh = null;
        this.user = null;
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('refresh');
      }
    },

    async login({ email, password }) {
      const response = await useApi.post('/auth/login', {
        body: {
          email,
          password,
          device_id: useDeviceId().getDeviceId(),
        },
      });

      if (response) {
        this.setAuth(response);

        return response;
      }

      return false;
    },

    logout() {
      this.setAuth(null);
    },

    async fetchUser() {
      const response = await useApi.get('/client/user/me').catch((error) => {
        // Every other failure here is an ordinary dead session and the caller
        // just lands on the login form. A block is the one that has to be said:
        // this runs from the global middleware, so without it a blocked person is
        // bounced to /login with nothing on screen. It arrives from the refused
        // token refresh in useApi — /client/user/me itself carries no block guard.
        const blocked = blockedMessage(error);
        if (blocked) {
          // The refresh stays refused for as long as the block holds, so this
          // session can never be renewed. Left in place, its token keeps the
          // global middleware calling fetchUser on every single navigation.
          this.setAuth(null);
          ElMessage.error({
            message: blocked,
            ...blockedMessageOptions(blocked),
            showClose: true,
          });
        }

        return false;
      });

      if (response) {
        this.user = response;

        return this.user;
      }

      return false;
    },
  },

  getters: {
    isLoggedIn(state) {
      return !!state.user;
    },
    isUserOrganization(state) {
      return state.user?.type === 2;
    },
    adminModules(state) {
      return state.user?.admin_modules || [];
    },
    // A non-superuser admin confined to a subset of admin modules.
    isRestrictedAdmin(state) {
      return !!state.user && !state.user.is_admin && this.adminModules.length > 0;
    },
    // Restricted admin whose user management is limited to organizations only.
    isOrganizationsScopedAdmin() {
      return this.isRestrictedAdmin && this.adminModules.includes('organizations');
    },
    // Route prefixes a restricted admin is allowed to visit (union of its modules).
    allowedAdminRoutes() {
      return _uniq(this.adminModules.flatMap((module) => ADMIN_MODULE_ROUTES[module] || []));
    },
    // Landing page for a restricted admin after login.
    adminLandingPath() {
      return this.allowedAdminRoutes[0] || '/requests';
    },
    canAccessAdminRoute() {
      return (path) => !!path && this.allowedAdminRoutes.some(
        (route) => path === route || path.startsWith(`${route}/`),
      );
    },
  },
});
